import { OmniElement, OmniStyleElement, html, css, classMap } from 'omni-ui';
import { produce } from 'immer';
import { om2alert } from 'omni-campaign-ui';
import './campaign-donut-chart.js';

OmniStyleElement.register();

/**
 * Class for Strategic Themes Module Element
 * @extends OmniElement
 */
export class CampaignStrategicThemes extends OmniElement {
  /**
   * Strategic Themes Lit Properties
   * @property {string} env - Application environment
   * @property {Object} context - Values passed in from wrapper/router
   * @property {string} token - API JWT token
   * @property {string} moduleTitle - title text for module block
   * @property {Object[]} savedData - Data already saved in the API
   * @property {string} totalPercentage - the total percentage of all combined themes; must be equal to 100 in order to save
   * @property {string} donutPlaceholder - the placeholder text for the input field inside the donut chart element
   */
  static get properties() {
    return {
      env: { type: String },
      context: { type: Object },
      token: { type: String },
      moduleTitle: { type: String },
      savedData: { type: Array },
      totalPercentage: { type: Number },
    };
  }

  /**
   * Strategic Themes module styles
   */
  static get styles() {
    return [
      super.styles,
      css`
        .omni .module-container {
          margin-bottom: 2.143rem;
          padding: 2.14rem 2.86rem 4.29rem 2.86rem;
          display: flex;
          flex-flow: column nowrap;
        }

        .body-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .control-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          margin-right: 3rem;
          flex-grow: 1;
          max-width: 28rem;
        }

        .omni .label {
          opacity: 0.6;
          font-size: 0.785rem;
          font-weight: normal;
          line-height: normal;
          letter-spacing: 0.0275rem;
          color: rgb(var(--rgb-core-dark));
          margin: 0px 0px 0.56rem 0.5rem !important;
        }

        .omni textarea {
          min-height: 4.286rem !important;
          overflow: hidden;
          min-width: 10rem !important;
          max-width: 25rem !important;
        }

        .strategy-box {
          display: flex;
          flex-direction: column;
          margin-bottom: 2.143rem;
          width: 100%;
        }

        .strategy-text {
          flex-grow: 1;
        }

        .user-inputs {
          display: flex;
          align-items: center;
          justify-content: stretch;
        }

        .percentage {
          max-width: 4.929rem;
          flex-shrink: 0;
          margin-left: 1rem;
          display: flex;
          align-items: center;
        }

        .omni .donut-theme {
          width: 45%;
          text-align: center;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }

        .omni .donut-theme:not(:placeholder-shown):not(:focus):not(:hover) {
          background-color: transparent;
          border: none;
          box-shadow: none;
          color: rgb(var(--rgb-core-dark));
        }

        .chart-container {
          position: relative;
          flex-grow: 1;
          max-width: 730px;
        }

        .total-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-weight: bold;
        }

        .omni .percentage-error {
          color: red;
        }
      `,
    ];
  }

  constructor() {
    super();
    this.strategicData = {};
    this.totalPercentage = 0;
  }

  /**
   * Initialize {@link #campaignsAPI} and {@link #objectsAPI} on `this` and calls {@link #_getTotalPercentage}
   * @see {@link CampaignsAPI}
   * @see {@link CentralizedDataAPI}
   * Checks if {@link #savedData} exists in the step and determines whether to use {@link #defaultData} or {@link #savedData}
   */
  connectedCallback() {
    super.connectedCallback();
    this.strategicData = !this.savedData?.strategies
      ? this.defaultData
      : { ...this.savedData?.strategies };
    this.donutTheme = this.savedData?.donutTheme;
    this._getTotalPercentage();
  }

  /**
   * Function to determine total percentage of all themes
   * Uses percentage key from {@link #strategicData}
   */
  _getTotalPercentage() {
    this.totalPercentage = Object.values(this.strategicData || {}).reduce(
      (acc, { percentage }) => {
        // eslint-disable-next-line no-param-reassign
        acc += Number(percentage);
        return acc;
      },
      0,
    );
  }

  /**
   * Handles any textarea and input field changes by firing {@link #_dispatchModuleChangedEvent} to notify step detail that {@link #strategicData} has been changed
   * @param {string} key - Determines associated key value pair that needs to be updated
   * @param {Object} target -
   * @param {string} type - the type of the input (string, number)
   */
  _handleChange(key, { target }, type) {
    if (type !== 'donutTheme') {
      this.strategicData = produce(this.strategicData, draft => {
        draft[key][type] =
          type === 'percentage' ? Number(target.value) : String(target.value);
      });
    } else {
      this.donutTheme = String(target.value);
    }
    this._dispatchModuleChangedEvent();
  }

  /**
   * @fires CampaignStrategicThemes~omni-module-changed
   * Calls {@link #_getTotalPercentage}
   */
  _dispatchModuleChangedEvent() {
    const { uuid_module: moduleId } = this.context;
    this._getTotalPercentage();

    /**
     * @event CampaignStrategicThemes~omni-module-changed
     * @type {object}
     * @property {string} moduleId - This module's ID
     * @property {function} saveAction - A reference to {@link submit} bound to this
     * @example
     *   this.dispatchNewEvent('omni-module-changed', {
     *     detail: {
     *       moduleId,
     *       saveAction: this.submit.bind(this),
     *     },
     *   });
     */
    this.dispatchNewEvent('omni-module-changed', {
      detail: {
        moduleId,
        saveAction: this._submit.bind(this),
        moduleType: 'strategic themes',
      },
    });
  }

  /**
   * Submit method passed up to {@link CampaignStepDetail} via {@link _dispatchModuleChangedEvent}
   */
  async _submit() {
    if (this.totalPercentage === 100) {
      await this.save({
        strategies: this.strategicData,
        donutTheme: this.donutTheme,
      });
    } else {
      om2alert(
        'Please make sure your percentages add up to 100% before saving.',
      );
    }
  }

  /**
   * Controls render of donut text box
   * @param {string} key - The value that {@link _handleChange} uses to determine the proper key to use - in this case 'donutTheme'
   */
  _renderDonutInput(key) {
    return html`<input
      ?readonly=${this.context.readonly}
      class="input donut-theme"
      type="text"
      required
      .value=${this.donutTheme ?? ''}
      .placeholder=${this.donutPlaceholder ??
      'What is the organizing strategic theme?'}
      @change=${target => this._handleChange(key, target, 'donutTheme')}
    />`;
  }

  /**
   * Renders individual "themes" and their associated textarea and percentage inputs
   * @param {string} key - Derived from {@link #strategicData} and used by {@link _handleChange} to retrive and save the proper data associated with said key
   * @param {string} subtitle - Key in {@link #strategicData}. Sets the given subtitle (label) for each theme
   * @param {string} placeholder - Key in {@link #strategicData}. Sets the given placeholder for each theme
   * @param {string} text - Key in {@link #strategicData}. Sets the given text for each theme
   * @param {string} percentage - Key in {@link #strategicData}. Sets the given percentage for each theme
   */
  _renderTheme(key, { subtitle, placeholder, text, percentage }) {
    return html`
      <div class="mb-5">
        <label class="label">${subtitle ?? ''}</label>
        <div
          class=${classMap({
            readonly: this.context.readonly,
            'user-inputs': true,
          })}
        >
          <div class="strategy-text">
            <textarea
              ?readonly=${this.context.readonly}
              class="textarea"
              required
              .value=${text ?? ''}
              placeholder=${placeholder}
              @change=${target => this._handleChange(key, target, 'text')}
            ></textarea>
          </div>
          <div class="percentage">
            <input
              ?readonly=${this.context.readonly}
              class="input"
              type="number"
              min="0"
              max="100"
              placeholder="20"
              required
              .value=${percentage ?? 20}
              @change=${target => this._handleChange(key, target, 'percentage')}
            />
            <span>%</span>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Renders all themes to page in set order determined by order key in {@link #strategicData}
   */
  _renderAllThemes() {
    return html`
      ${Object.keys(this.strategicData || {})
        .sort(
          (keya, keyb) =>
            this.strategicData[keya].order - this.strategicData[keyb].order,
        )
        .map(key => this._renderTheme(key, this.strategicData[key]))}
    `;
  }

  /**
   * @returns {html} Rendered HTML for the component
   */
  render() {
    // if (this.context.readonly && !this.savedData?.strategies) return null;
    return html`
      <omni-style>
        <div class="box module-container">
          <div class="header-container">
            <h2 class="title mb-5 is-3">${this.moduleTitle ?? ''}</h2>
          </div>
          <div class="body-container">
            <div class="control-container">
              <form>
                <span class="field m-0">${this._renderAllThemes()}</span>
                <div class="total-container">
                  <div>Total</div>
                  <div class="percentage">
                    <input
                      class=${classMap({
                        input: true,
                        'percentage-error': this.totalPercentage !== 100,
                      })}
                      type="text"
                      .value=${this.totalPercentage?.toString() ?? '0'}
                      readonly
                    />
                    <span
                      class=${classMap({
                        'percentage-error': this.totalPercentage !== 100,
                      })}
                      >%</span
                    >
                  </div>
                </div>
              </form>
            </div>

            <div class="chart-container">
              <campaign-donut-chart .strategicData=${this.strategicData}>
              </campaign-donut-chart>
              ${this._renderDonutInput('donutTheme')}
            </div>
          </div>
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-strategic-themes', CampaignStrategicThemes);
